/**
 * Save Pro Calculation Section Component
 *
 * Provides UI for saving Advanced Calculator calculations.
 * Similar to SaveCalculationSection but adapted for Advanced Calculator data structure.
 *
 * Features:
 * - Authentication state detection
 * - Save/Update calculation functionality
 * - Navigation to login/signup pages
 * - Success/error message display
 */

import {
  Paper,
  Stack,
  Text,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import {
  IconDeviceFloppy,
  IconUserPlus,
  IconLogin,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import type { ProCalculatorFormValues, ProCalculatorResults } from '../../../../types/proCalculator';

/**
 * Component props interface
 */
interface SaveProCalculationSectionProps {
  /** Advanced Calculator input data */
  formValues: ProCalculatorFormValues;

  /** Advanced Calculator results */
  results: ProCalculatorResults;

  /** Callback when user saves calculation */
  onSave: () => void;

  /** Whether save operation is in progress */
  isSaving?: boolean;

  /** Error message from save operation */
  saveError?: string;

  /** Whether save was successful */
  saveSuccess?: boolean;

  /** Whether calculation already exists for this year */
  calculationExists?: boolean;
}

/**
 * Save Pro Calculation Section Component
 *
 * Displays different UI based on authentication state:
 * - Unauthenticated: Shows signup/login buttons
 * - Authenticated: Shows save button
 */
export function SaveProCalculationSection({
  formValues,
  results: _results,
  onSave,
  isSaving = false,
  saveError,
  saveSuccess = false,
  calculationExists = false,
}: SaveProCalculationSectionProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  /**
   * Handle save button click
   * Calls parent callback
   */
  const handleSave = () => {
    onSave();
  };

  /**
   * Handle signup button click
   * Navigate to signup page
   */
  const handleSignup = () => {
    void navigate('/signup');
  };

  /**
   * Handle login button click
   * Navigate to login page
   */
  const handleLogin = () => {
    void navigate('/login');
  };

  return (
    <Paper
      p="xl"
      shadow="sm"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-blue-3)',
        backgroundColor: 'var(--mantine-color-blue-0)',
      }}
    >
      <Stack gap="md">
        {/* Header with Icon */}
        <Group gap="sm">
          <IconDeviceFloppy size={28} color="var(--mantine-color-blue-6)" />
          <Text size="xl" fw={600} c="blue.8">
            Zapisz swoją kalkulację profesjonalną
          </Text>
        </Group>

        {/* Description - Different for authenticated vs unauthenticated */}
        {!isAuthenticated && (
          <Text size="sm" c="dimmed">
            Zarejestruj się, aby zapamiętać swoje szczegółowe kalkulacje na przyszłość
            i móc wrócić do nich w dowolnym momencie.
          </Text>
        )}

        {isAuthenticated && (
          <Text size="sm" c="dimmed">
            Zapisz swoją szczegółową kalkulację profesjonalną, aby móc wrócić do niej później.
            Twoje miesięczne dane, wzorce godzinowe i wyniki porównań będą bezpiecznie przechowane.
          </Text>
        )}

        {/* Success Message */}
        {saveSuccess && (
          <Alert
            icon={<IconCheck size={18} />}
            color="green"
            variant="light"
            title={calculationExists ? "Kalkulacja zaktualizowana" : "Kalkulacja zapisana"}
          >
            <Text size="sm">
              Twoja kalkulacja profesjonalna została pomyślnie {calculationExists ? 'zaktualizowana' : 'zapisana'}.
            </Text>
          </Alert>
        )}

        {/* Error Message */}
        {saveError && (
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="red"
            variant="light"
            title="Błąd podczas zapisywania"
          >
            <Text size="sm">{saveError}</Text>
          </Alert>
        )}

        {/* Unauthenticated State */}
        {!isAuthenticated && (
          <Group gap="md" mt="xs">
            <Button
              leftSection={<IconUserPlus size={18} />}
              onClick={handleSignup}
              variant="filled"
              color="blue"
              size="md"
            >
              Zarejestruj się
            </Button>
            <Button
              leftSection={<IconLogin size={18} />}
              onClick={handleLogin}
              variant="outline"
              color="blue"
              size="md"
            >
              Zaloguj się
            </Button>
          </Group>
        )}

        {/* Authenticated State */}
        {isAuthenticated && (
          <Stack gap="md">
            {/* Greeting */}
            <Text size="md" fw={500} c="blue.8">
              Witaj!
            </Text>

            {/* Save Button */}
            <Group gap="md">
              <Button
                leftSection={<IconDeviceFloppy size={18} />}
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
                variant="filled"
                color="blue"
                size="md"
              >
                {calculationExists ? 'Zaktualizuj kalkulację' : 'Zapisz kalkulację'}
              </Button>
            </Group>

            {/* Information about saved data */}
            <Text size="xs" c="dimmed">
              Zapisujemy Twoje szczegółowe dane miesięczne, wzorce godzinowe, parametry faktur
              oraz wyniki porównania dla roku {formValues.calculationYear}.
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
